import { useEffect, useRef, useState } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import HeroCarousel from './hero-carousel';

interface CounterProps {
  value: number;
  suffix: string;
}

interface StatType {
  value: number;
  label: string;
  suffix: string;
}

const Hero = () => {
  const stats: StatType[] = [
    { value: 100, label: 'Userbase', suffix: 'K+' },
    { value: 80, label: 'Time saved', suffix: '%' },
    { value: 15, label: 'AI tools', suffix: '+' },
  ];

  const statsRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(statsRef, { once: true, margin: '-100px' });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    }
  }, [isInView, controls]);

  const Counter = ({ value, suffix }: CounterProps) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
      let timer: NodeJS.Timeout;
      if (isInView) {
        let start = 0;
        const end = value;
        const duration = 2000; // 2 seconds
        const increment = end / (duration / 16); // 16ms per frame

        timer = setInterval(() => {
          start += increment;
          if (start > end) {
            setCount(end);
            clearInterval(timer);
          } else {
            setCount(Math.floor(start));
          }
        }, 16);
      }

      return () => clearInterval(timer);
    }, [isInView, value]);

    return (
      <span className="text-3xl sm:text-5xl md:text-6xl font-bold text-primary tracking-wider">
        {count}
        {suffix}
      </span>
    );
  };

  return (
    <div className="bg-background-marketing text-text-primary min-h-[60vh] sm:min-h-[70vh] md:min-h-[80vh] overflow-hidden flex flex-col items-center text-center px-4 py-12 pt-0 sm:py-16 md:py-20">
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className="text-4xl sm:text-5xl md:text-6xl max-md:pt-12 font-medium max-w-[350px] md:max-w-3xl leading-tight sm:leading-1.1"
      >
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          One Platform,
        </motion.span>
        <br className="hidden sm:block" />
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="sm:mt-2 whitespace-nowrap inline-block"
        >
          Infinite Possibilities
        </motion.span>
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
        className="text-text-secondary tracking-wide mt-3 sm:mt-4 text-base sm:text-lg md:text-xl max-w-[300px] sm:max-w-xl"
      >
        Unlock your imagination with Kreaitor.ai
      </motion.p>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.8 }}
        className="w-full max-w-[90vw] sm:max-w-[85vw] md:max-w-[80vw] mt-8 sm:mt-12"
      >
        <HeroCarousel />
      </motion.div>

      {/* Stats Section */}
      <motion.div
        ref={statsRef}
        animate={controls}
        variants={{
          hidden: { opacity: 0, y: 20 },
          visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.8, staggerChildren: 0.2 },
          },
          delay: { opacity: 0, y: 20, transition: { duration: 0.8 } },
        }}
        className="mt-12 sm:mt-16 overflow-x-auto"
      >
        <div className="flex flex-row justify-start sm:justify-center gap-4 sm:gap-16 md:gap-28 min-w-max sm:min-w-0 sm:flex-wrap px-4 sm:px-0">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 },
              }}
              className="flex flex-col items-center"
            >
              <Counter value={stat.value} suffix={stat.suffix} />
              <motion.span
                variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
                className="text-accent mt-1 text-md sm:text-xl tracking-wider"
              >
                {stat.label}
              </motion.span>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default Hero;
